import React from 'react';
import { Typography } from '../Typography';
import styles from './FeaturedBlogCard.module.scss';
import BlogImage from '../BlogSection/BlogImage/BlogImage';

interface FeaturedBlogCardProps {
  post: {
    id: number | string;
    image: string;
    category: string;
    readTime: string;
    title: string;
    description: string;
    author: {
      name: string;
      avatar: string;
      date: string;
    };
    slug?: string;
    body?: string;
  };
  noBorderRadius?: boolean;
}

const FeaturedBlogCard: React.FC<FeaturedBlogCardProps> = ({ post, noBorderRadius = false }) => {
  // Function to check if body has actual content (not just empty HTML tags)
  const hasActualContent = (htmlContent?: string): boolean => {
    if (!htmlContent) return false;

    // Remove all HTML tags using regex (works in both client and server)
    const textContent = htmlContent.replace(/<[^>]*>/g, '');

    // Decode HTML entities
    const decodedContent = textContent
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Remove all whitespace and check if there's actual content
    const cleanedContent = decodedContent.trim().replace(/\s+/g, ' ');

    return cleanedContent.length > 0;
  };

  const hasBody = hasActualContent(post.body);
  const isClickable = hasBody && post.slug;

  const handleClick = () => {
    if (isClickable) {
      window.location.href = `/news/${post.slug}`;
    }
  };

  return (
    <div
      className={`${styles.featuredCard} ${
        noBorderRadius ? styles.noBorderRadius : ''
      } ${isClickable ? styles.clickable : styles.notClickable}`}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {/* Image Section (60% width) */}
      <div className={`${styles.imageSection} ${noBorderRadius ? styles.noBorderRadius : ''}`}>
        <BlogImage src={post.image} alt={post.title} />
      </div>

      {/* Content Section (40% width) */}
      <div className={styles.contentSection}>
        {isClickable && (
          <div className={styles.cardTags}>
            <Typography
              variant="text-xs"
              as="span"
              className={styles.tag}
              style={{ color: '#D6BBFB' }}
            >
              {post.category}
            </Typography>
            <Typography
              variant="text-xs"
              as="span"
              className={styles.readTime}
              style={{ color: '#D6BBFB' }}
            >
              {post.readTime}
            </Typography>
          </div>
        )}

        <div className={styles.titleWrapper}>
          <Typography variant="display-xs" as="h2" color="primary" className={styles.title}>
            {post.title}
          </Typography>
        </div>

        <div className={styles.descriptionWrapper}>
          <Typography variant="text-md" as="p" color="grayDark-400" className={styles.description}>
            {post.description}
          </Typography>
        </div>

        <div className={styles.cardAuthor}>
          <div className={styles.authorAvatar}>
            <img src={post.author.avatar} alt={post.author.name} />
          </div>
          <div className={styles.authorInfo}>
            <Typography variant="text-sm" as="span" color="primary" weight="medium">
              {post.author.name}
            </Typography>
            <Typography variant="text-sm" as="span" color="grayDark-400">
              {post.author.date}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedBlogCard;
